/**
 * "View as user" button — drops the current super-admin into the target
 * user's session for QA / debug / training. Read+write mode, 60-min TTL.
 *
 * Renders nothing unless ALL of the following are true:
 *  - the current session user is a super-admin (full admin in ATEK org),
 *  - no impersonation is currently active,
 *  - the target user is not the current session user.
 *
 * On confirm: POST /v4/auth/impersonate/:targetUserId then hard-reload
 * to root so every cached query is rebuilt under the target's identity.
 */

import { useState } from 'react';
import { Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSession, useIsImpersonating } from '@/hooks/useSession';
import { isFullAdmin } from '@/types/session';
import { api, ApiError } from '@/lib/api';

interface ImpersonateUserButtonProps {
  /** Target user's MongoDB _id (24-char ObjectId). */
  targetUserId: string;
  /** Display name shown in the confirm modal. */
  targetLabel: string;
  /** Optional Tailwind classes / variant overrides. */
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  size?: 'default' | 'sm' | 'icon';
  /** When true, renders just the eye icon (no label). */
  iconOnly?: boolean;
}

export function ImpersonateUserButton({
  targetUserId,
  targetLabel,
  variant = 'outline',
  size = 'sm',
  iconOnly = false,
}: ImpersonateUserButtonProps) {
  const { user } = useSession();
  const isImpersonating = useIsImpersonating();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isSuperAdmin = isFullAdmin(user);
  const isSelf = user?._id === targetUserId;

  if (!isSuperAdmin || isImpersonating || isSelf) return null;

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await api.post(`/v4/auth/impersonate/${targetUserId}`, { reason: reason.trim() });
      toast.success(`Now viewing as ${targetLabel}`);
      // Hard reload so every TanStack Query cache is rebuilt under the
      // target's identity (sidebar, RBAC, dashboards, etc.).
      window.location.assign('/');
    } catch (err) {
      const message = err instanceof ApiError
        ? (typeof err.data === 'object' && err.data && 'error' in err.data
          ? String((err.data as { error: unknown }).error)
          : err.message)
        : 'Failed to start impersonation';
      toast.error(message);
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={`View as ${targetLabel}`}
      >
        <Eye className="h-4 w-4" />
        {!iconOnly && <span className="ml-2">View as</span>}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!submitting) setOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>View platform as {targetLabel}?</DialogTitle>
            <DialogDescription>
              You will be logged into the platform as this user for up to 60 minutes.
              You can write data on their behalf — every action will be attributed to
              them in audit logs, with your identity recorded as the impersonator.
              You can exit at any time from the amber banner at the top.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="impersonation-reason">Reason (optional)</Label>
            <Input
              id="impersonation-reason"
              placeholder="e.g. QA ticket #1234, validating new role…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              autoFocus
              disabled={submitting}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirm} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Start viewing as {targetLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ImpersonateUserButton;
